import { TFunction } from 'i18next';
import { endOfDay, isAfter, isThisMonth, isThisWeek, isToday } from 'date-fns';

import { LoggedUser, User } from 'entities/modules/types';
import { FullProject, TaskWithSub } from '../types';
import { taskStatusByCode } from '../configs';

export type TaskOverviewTab = 'overdue' | 'today' | 'week' | 'month';

export const taskOverviewConfig = {
  overdue: {
    getLabel: (t: TFunction) => t('overdue-tasks'),
    tab: 'overdue',
    id: 'overdue',
    checker: ({ expectedDate, status }: TaskWithSub) =>
      !!(
        expectedDate &&
        taskStatusByCode[status].isActiveStatus &&
        isAfter(new Date(), endOfDay(new Date(expectedDate)))
      ),
  },

  today: {
    getLabel: (t: TFunction) => t('todays-tasks'),
    tab: 'today',
    id: 'today',
    checker: ({ expectedDate, status }: TaskWithSub) =>
      !!(
        expectedDate &&
        taskStatusByCode[status].isActiveStatus &&
        isToday(new Date(expectedDate))
      ),
  },

  week: {
    getLabel: (t: TFunction) => t('this-week'),
    tab: 'week',
    id: 'week',
    checker: ({ expectedDate, status }: TaskWithSub) =>
      !!(
        expectedDate &&
        taskStatusByCode[status].isActiveStatus &&
        isThisWeek(new Date(expectedDate))
      ),
  },

  month: {
    getLabel: (t: TFunction) => t('this-month'),
    tab: 'month',
    id: 'month',
    checker: ({ expectedDate, status }: TaskWithSub) =>
      !!(
        expectedDate &&
        taskStatusByCode[status].isActiveStatus &&
        isThisMonth(new Date(expectedDate))
      ),
  },
};

interface TaskOverviewParams {
  projects: FullProject[];
  loggedUser: LoggedUser;
  teamUsers: User[];
}

export function parseTasksToOverview({
  loggedUser,
  projects,
  teamUsers,
}: TaskOverviewParams): Record<TaskOverviewTab, TaskWithSub[]> {
  const tasksToEvaluate = projects
    .map(({ tasks }) => {
      const usersToGetTasks = loggedUser.type === 'LEADER' ? teamUsers : [loggedUser];

      const teamTasks = usersToGetTasks
        .map(({ id }) => {
          return tasks.filter(({ owner }) => owner.id === id);
        })
        .flat();

      return teamTasks;
    })
    .flat();

  return Object.fromEntries(
    Object.entries(taskOverviewConfig).map(([key, { checker }]) => [
      key,
      tasksToEvaluate
        .filter((task) => checker(task))
        .sort(
          (a, b) =>
            Number(new Date(a.expectedDate as string)) - Number(new Date(b.expectedDate as string)),
        ),
    ]),
  ) as Record<TaskOverviewTab, TaskWithSub[]>;
}
